// FuelTool.js

import React, { useState, useEffect } from "react";
import axios from "axios";

function FuelTool() {
  const [cadPerLiter, setCadPerLiter] = useState("");
  const [usdPerGallon, setUsdPerGallon] = useState("");
  const [rate, setRate] = useState(null);
  const [primaryCurrency, setPrimaryCurrency] = useState("CAD");
  const [fuelUnit, setFuelUnit] = useState("litres");
  const [tankLitres, setTankLitres] = useState("");

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const response = await axios.get(
          "https://api.exchangerate-api.com/v4/latest/USD",
        );
        setRate(response.data.rates.CAD);
      } catch (error) {
        console.error("Error fetching rate:", error);
      }
    };
    fetchRate();
    if (window.gtag && window.consented)
      window.gtag("event", "tool_view", { tool: "fuel_tool" });

    // Load preferences
    setPrimaryCurrency(localStorage.getItem("primaryCurrency") || "CAD");
    setFuelUnit(localStorage.getItem("fuelUnit") || "litres");
    setTankLitres(localStorage.getItem("tankLitres") || "");
    setCadPerLiter(localStorage.getItem("cadPerLiter") || "");
    setUsdPerGallon(localStorage.getItem("usdPerGallon") || "");
  }, []);

  const saveTankLitres = (value) => {
    setTankLitres(value);
    localStorage.setItem("tankLitres", value);
    if (window.gtag && window.consented)
      window.gtag("event", "preference_changed", {
        preference: "tank_litres",
        value: value,
      });
  };

  const saveFuelUnit = (value) => {
    setFuelUnit(value);
    localStorage.setItem("fuelUnit", value);
    if (window.gtag && window.consented)
      window.gtag("event", "preference_changed", {
        preference: "fuel_unit",
        value: value,
      });
  };

  const calculateFillUpCosts = (fillPercentage) => {
    if (!cadPerLiter || !usdPerGallon || !rate || !tankLitres) return null;

    const fillLitres = (parseFloat(tankLitres) * fillPercentage) / 100;
    const fillGallons = fillLitres / 3.78541;

    // Read preferences directly from localStorage to always use current values
    const cadCashback = parseFloat(localStorage.getItem("cadCashback")) || 0;
    const usdCashback = parseFloat(localStorage.getItem("usdCashback")) || 0;
    const usdFxFee = parseFloat(localStorage.getItem("usdFxFee")) || 0;
    const cadFxFee = parseFloat(localStorage.getItem("cadFxFee")) || 0;

    const costCAD =
      parseFloat(cadPerLiter) * fillLitres * (1 - cadCashback / 100);
    const costUSD =
      parseFloat(usdPerGallon) * fillGallons * (1 - usdCashback / 100);

    // Apply fees to get final costs in original currencies
    const finalCostCAD = costCAD * (1 + cadFxFee / 100);
    const finalCostUSD = costUSD * (1 + usdFxFee / 100);

    // Convert both to primary currency for comparison and savings calculation
    const cadCostInPrimary =
      primaryCurrency === "USD" ? finalCostCAD / rate : finalCostCAD;
    const usdCostInPrimary =
      primaryCurrency === "USD" ? finalCostUSD : finalCostUSD * rate;

    return {
      canada: finalCostCAD.toFixed(2),
      us: finalCostUSD.toFixed(2),
      canadaCurrency: "CAD",
      usCurrency: "USD",
      volume:
        fuelUnit === "litres" ? fillLitres.toFixed(1) : fillGallons.toFixed(1),
      volumeUnit: fuelUnit === "litres" ? "L" : "gal",
      cheaper: cadCostInPrimary < usdCostInPrimary ? "canada" : "us",
      savings: Math.abs(cadCostInPrimary - usdCostInPrimary).toFixed(2),
      cheaperLocation: cadCostInPrimary < usdCostInPrimary ? "Canada" : "US",
    };
  };

  return (
    <section className="utility canada-theme us-theme">
      <h2>Fuel Tool</h2>

      {/* Fuel Unit Preference */}
      <div className="preference-item" style={{ marginBottom: "20px" }}>
        <label>Fuel Unit Display</label>
        <select value={fuelUnit} onChange={(e) => saveFuelUnit(e.target.value)}>
          <option value="litres">Litres</option>
          <option value="gallons">Gallons</option>
        </select>
      </div>

      {/* Tank Size Input */}
      <div className="preference-item" style={{ marginBottom: "20px" }}>
        <label>
          Fuel Tank Size ({fuelUnit === "litres" ? "Litres" : "Gallons"})
        </label>
        <input
          type="number"
          step="0.1"
            style={{ width: "80%" }}
          value={
            fuelUnit === "litres"
              ? tankLitres
              : tankLitres
                ? (parseFloat(tankLitres) / 3.78541).toFixed(1)
                : ""
          }
          onChange={(e) => {
            const value = e.target.value;
            if (fuelUnit === "gallons" && value) {
              // Convert gallons to litres for storage
              const litres = (parseFloat(value) * 3.78541).toFixed(1);
              saveTankLitres(litres);
            } else {
              saveTankLitres(value);
            }
          }}
          placeholder={fuelUnit === "litres" ? "e.g. 60" : "e.g. 16"}
        />
      </div>

      {/* Gas Price Inputs */}
      <div className="conversion-row">
        <div className="left">
          <label>$CAD / L</label>
          <input
            type="number"
            step="0.01"
            value={cadPerLiter}
            onChange={(e) => {
              setCadPerLiter(e.target.value);
              localStorage.setItem("cadPerLiter", e.target.value);
            }}
            className="tall-input"
            placeholder="Enter Canadian price (e.g. 1.45)"
          />
        </div>
        <div className="right">
          <label>$USD / gal</label>
          <input
            type="number"
            step="0.01"
            value={usdPerGallon}
            onChange={(e) => {
              setUsdPerGallon(e.target.value);
              localStorage.setItem("usdPerGallon", e.target.value);
            }}
            className="tall-input"
            placeholder="Enter US price (e.g. 3.25)"
          />
        </div>
      </div>

      {/* Fill-up Cost Table */}
      {cadPerLiter && usdPerGallon && rate && tankLitres && (
        <div style={{ marginTop: "20px" }}>
          <h3>Fill-up Costs</h3>
          <table className="gas-tool-table">
            <thead>
              <tr>
                <th>Fill Level</th>
                <th>Volume</th>
                <th>Canada (CAD)</th>
                <th>US (USD)</th>
              </tr>
            </thead>
            <tbody>
              {[25, 50, 75, 100].map((percentage) => {
                const costs = calculateFillUpCosts(percentage);
                return (
                  <tr key={percentage}>
                    <td>
                      {percentage === 25
                        ? "¼ Tank"
                        : percentage === 50
                          ? "½ Tank"
                          : percentage === 75
                            ? "¾ Tank"
                            : "Full Tank"}
                    </td>
                    <td>
                      {costs.volume} {costs.volumeUnit}
                    </td>
                    <td
                      style={
                        costs.cheaper === "canada"
                          ? { color: "#4CAF50", fontWeight: "bold" }
                          : {}
                      }
                    >
                      ${costs.canada} {costs.canadaCurrency}
                    </td>
                    <td
                      style={
                        costs.cheaper === "us"
                          ? { color: "#4CAF50", fontWeight: "bold" }
                          : {}
                      }
                    >
                      ${costs.us} {costs.usCurrency}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Contributing Factors Display */}
          <div
            style={{
              marginTop: "10px",
              fontStyle: "italic",
              fontSize: "0.9em",
              color: "#666",
            }}
          >
            <p style={{ margin: "5px 0" }}>
              <strong>CAD:</strong>{" "}
              {parseFloat(localStorage.getItem("cadCashback")) || 0}% cashback,{" "}
              {parseFloat(localStorage.getItem("cadFxFee")) || 0}% FX fee
              applied
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>USD:</strong>{" "}
              {parseFloat(localStorage.getItem("usdCashback")) || 0}% cashback,{" "}
              {parseFloat(localStorage.getItem("usdFxFee")) || 2.5}% FX fee
              applied
            </p>
          </div>

          {/* Savings Display */}
          {(() => {
            const fullTankCosts = calculateFillUpCosts(100);
            if (fullTankCosts) {
              return (
                <div
                  style={{
                    marginTop: "15px",
                    padding: "15px",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    border: "2px solid #4CAF50",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <h4 style={{ margin: "0 0 10px 0", color: "#4CAF50" }}>
                    💰 Full Tank Savings
                  </h4>
                  <div
                    style={{
                      fontSize: "1.2em",
                      fontWeight: "bold",
                      color: "#4CAF50",
                    }}
                  >
                    Save ${fullTankCosts.savings} {primaryCurrency} by filling
                    up in {fullTankCosts.cheaperLocation}!
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </section>
  );
}

export default FuelTool;
